/**
 * Windows-style editable region selection over the source photo (ADR-0006).
 *
 * VS-SLICE-8: the box is now fully editable by hand — 8 resize handles and
 * drag-the-inside-to-move — so the user can shape the region until it frames
 * exactly what they want before any conversion runs. The model/algorithm side
 * can also set the box (see `setSelection`), after which the user refines it.
 *
 * All geometry stays in native source pixels; the overlay is drawn in viewport
 * pixels using the canvas CSS box as the image area.
 */

import { moveRect, resizeRect, viewportRectToSourceRect, type Rect, type ResizeHandle } from './regionCrop.ts';

export interface SourceMarqueeCallbacks {
  onSelectionChange?: (rect: Rect | null) => void;
}

const MIN_SOURCE_PX = 8;
const HANDLE_HIT_PX = 10;
const HANDLES: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

type Gesture =
  | { kind: 'new'; start: { x: number; y: number } }
  | { kind: 'move'; last: { x: number; y: number } }
  | { kind: 'resize'; handle: ResizeHandle };

export class SourceMarquee {
  private readonly canvas: HTMLCanvasElement;
  private readonly host: HTMLElement;
  private readonly callbacks: SourceMarqueeCallbacks;
  private readonly box: HTMLDivElement;
  private readonly handleEls: Map<ResizeHandle, HTMLDivElement> = new Map();

  private sourceWidth = 0;
  private sourceHeight = 0;
  private activePointerId: number | null = null;
  private gesture: Gesture | null = null;
  private selection: Rect | null = null;

  private readonly onPointerDown = (ev: PointerEvent) => this.handleDown(ev);
  private readonly onPointerMove = (ev: PointerEvent) => this.handleMove(ev);
  private readonly onPointerUp = (ev: PointerEvent) => this.handleUp(ev);
  private readonly onWindowResize = () => this.redraw();

  constructor(canvas: HTMLCanvasElement, host: HTMLElement, callbacks: SourceMarqueeCallbacks = {}) {
    this.canvas = canvas;
    this.host = host;
    this.callbacks = callbacks;

    if (typeof getComputedStyle === 'function' && getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }

    const box = document.createElement('div');
    box.className = 'p2i-source-marquee';
    box.style.display = 'none';
    box.setAttribute('aria-hidden', 'true');
    host.appendChild(box);
    this.box = box;

    for (const handle of HANDLES) {
      const el = document.createElement('div');
      el.className = 'p2i-source-handle';
      el.dataset.handle = handle;
      el.style.display = 'none';
      host.appendChild(el);
      this.handleEls.set(handle, el);
    }

    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onWindowResize);
    }
  }

  public setSourceSize(width: number, height: number): void {
    this.sourceWidth = width;
    this.sourceHeight = height;
    this.redraw();
  }

  public getSelection(): Rect | null {
    return this.selection;
  }

  /** Used by the algorithm proposal ("Bölge Öner") and by tests. */
  public setSelection(rect: Rect | null): void {
    this.selection = rect;
    this.redraw();
    this.callbacks.onSelectionChange?.(rect);
  }

  public clear(): void {
    this.selection = null;
    this.gesture = null;
    this.box.style.display = 'none';
    for (const el of this.handleEls.values()) el.style.display = 'none';
    this.callbacks.onSelectionChange?.(null);
  }

  public redraw(): void {
    if (!this.selection || this.sourceWidth <= 0 || this.sourceHeight <= 0) {
      this.box.style.display = 'none';
      for (const el of this.handleEls.values()) el.style.display = 'none';
      return;
    }
    const origin = this.imageOrigin();
    if (!origin) return;
    const { originX, originY, width, height } = origin;

    const left = originX + (this.selection.x / this.sourceWidth) * width;
    const top = originY + (this.selection.y / this.sourceHeight) * height;
    const right = originX + ((this.selection.x + this.selection.width) / this.sourceWidth) * width;
    const bottom = originY + ((this.selection.y + this.selection.height) / this.sourceHeight) * height;

    this.box.style.display = 'block';
    this.box.style.left = `${left}px`;
    this.box.style.top = `${top}px`;
    this.box.style.width = `${Math.max(2, right - left)}px`;
    this.box.style.height = `${Math.max(2, bottom - top)}px`;

    const cx = (left + right) / 2;
    const cy = (top + bottom) / 2;
    const positions: Record<ResizeHandle, { x: number; y: number }> = {
      nw: { x: left, y: top }, n: { x: cx, y: top }, ne: { x: right, y: top },
      e: { x: right, y: cy }, se: { x: right, y: bottom }, s: { x: cx, y: bottom },
      sw: { x: left, y: bottom }, w: { x: left, y: cy },
    };
    for (const [handle, el] of this.handleEls) {
      const p = positions[handle];
      el.style.display = 'block';
      el.style.left = `${p.x - 5}px`;
      el.style.top = `${p.y - 5}px`;
    }
  }

  public detach(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onWindowResize);
    }
    if (this.box.parentNode) this.box.parentNode.removeChild(this.box);
    for (const el of this.handleEls.values()) {
      if (el.parentNode) el.parentNode.removeChild(el);
    }
  }

  // -------------------------------------------------------------------------

  private hostPoint(ev: PointerEvent): { x: number; y: number } {
    const hostRect = this.host.getBoundingClientRect();
    return { x: ev.clientX - hostRect.left, y: ev.clientY - hostRect.top };
  }

  private imageOrigin(): { originX: number; originY: number; width: number; height: number } | null {
    const canvasRect = this.canvas.getBoundingClientRect();
    const hostRect = this.host.getBoundingClientRect();
    if (canvasRect.width <= 0 || canvasRect.height <= 0) return null;
    return {
      originX: canvasRect.left - hostRect.left,
      originY: canvasRect.top - hostRect.top,
      width: canvasRect.width,
      height: canvasRect.height,
    };
  }

  private handleAt(point: { x: number; y: number }): ResizeHandle | null {
    if (!this.selection) return null;
    for (const [handle, el] of this.handleEls) {
      if (el.style.display === 'none') continue;
      const elRect = el.getBoundingClientRect();
      const hostRect = this.host.getBoundingClientRect();
      const cx = elRect.left - hostRect.left + elRect.width / 2;
      const cy = elRect.top - hostRect.top + elRect.height / 2;
      if (Math.abs(point.x - cx) <= HANDLE_HIT_PX && Math.abs(point.y - cy) <= HANDLE_HIT_PX) {
        return handle;
      }
    }
    return null;
  }

  /** Host-relative point -> native source pixel point. */
  private viewportToSourcePoint(point: { x: number; y: number }): { x: number; y: number } | null {
    const origin = this.imageOrigin();
    if (!origin || this.sourceWidth <= 0 || this.sourceHeight <= 0) return null;
    const localX = point.x - origin.originX;
    const localY = point.y - origin.originY;
    if (localX < 0 || localY < 0 || localX > origin.width || localY > origin.height) return null;
    return {
      x: (localX / origin.width) * this.sourceWidth,
      y: (localY / origin.height) * this.sourceHeight,
    };
  }

  private handleDown(ev: PointerEvent): void {
    if (this.sourceWidth <= 0 || this.sourceHeight <= 0) return;
    if (this.activePointerId !== null) return;
    if (typeof ev.button === 'number' && ev.button !== 0) return;

    const point = this.hostPoint(ev);
    let gesture: Gesture;

    const handle = this.handleAt(point);
    if (handle) {
      gesture = { kind: 'resize', handle };
    } else if (this.selection && this.insideSelection(point)) {
      gesture = { kind: 'move', last: point };
    } else {
      gesture = { kind: 'new', start: point };
    }

    this.activePointerId = ev.pointerId;
    this.gesture = gesture;
    try {
      this.canvas.setPointerCapture(ev.pointerId);
    } catch (_) {}
    ev.preventDefault();
    ev.stopPropagation();
  }

  private handleMove(ev: PointerEvent): void {
    if (this.activePointerId !== ev.pointerId || !this.gesture) return;
    const point = this.hostPoint(ev);

    if (this.gesture.kind === 'new') {
      const sourceRect = this.mapViewportRect(this.gesture.start, point);
      this.selection = sourceRect;
      this.redraw();
      this.callbacks.onSelectionChange?.(sourceRect);
      return;
    }

    if (this.gesture.kind === 'move') {
      const from = this.viewportToSourcePoint(this.gesture.last);
      const to = this.viewportToSourcePoint(point);
      if (from && to && this.selection) {
        this.selection = moveRect(this.selection, to.x - from.x, to.y - from.y);
        this.selection = this.clampSelection(this.selection);
        this.redraw();
        this.callbacks.onSelectionChange?.(this.selection);
      }
      this.gesture.last = point;
      return;
    }

    // resize
    const sourcePoint = this.viewportToSourcePoint(point);
    if (sourcePoint && this.selection) {
      this.selection = this.clampSelection(
        resizeRect(this.selection, this.gesture.handle, sourcePoint, MIN_SOURCE_PX)
      );
      this.redraw();
      this.callbacks.onSelectionChange?.(this.selection);
    }
  }

  private handleUp(ev: PointerEvent): void {
    if (this.activePointerId !== ev.pointerId) return;
    try {
      this.canvas.releasePointerCapture(ev.pointerId);
    } catch (_) {}
    this.activePointerId = null;
    this.gesture = null;

    if (this.selection && (this.selection.width < MIN_SOURCE_PX || this.selection.height < MIN_SOURCE_PX)) {
      this.clear();
    }
  }

  private insideSelection(point: { x: number; y: number }): boolean {
    const origin = this.imageOrigin();
    if (!origin || !this.selection) return false;
    const left = origin.originX + (this.selection.x / this.sourceWidth) * origin.width;
    const top = origin.originY + (this.selection.y / this.sourceHeight) * origin.height;
    const right = left + (this.selection.width / this.sourceWidth) * origin.width;
    const bottom = top + (this.selection.height / this.sourceHeight) * origin.height;
    return point.x >= left && point.x <= right && point.y >= top && point.y <= bottom;
  }

  private mapViewportRect(a: { x: number; y: number }, b: { x: number; y: number }): Rect | null {
    const origin = this.imageOrigin();
    if (!origin) return null;
    return viewportRectToSourceRect(
      {
        offsetX: 0,
        offsetY: 0,
        displayWidth: origin.width,
        displayHeight: origin.height,
        sourceWidth: this.sourceWidth,
        sourceHeight: this.sourceHeight,
      },
      {
        x: Math.min(a.x, b.x) - origin.originX,
        y: Math.min(a.y, b.y) - origin.originY,
        width: Math.abs(b.x - a.x),
        height: Math.abs(b.y - a.y),
      },
      MIN_SOURCE_PX
    );
  }

  private clampSelection(rect: Rect): Rect {
    const width = Math.min(Math.max(MIN_SOURCE_PX, Math.round(rect.width)), this.sourceWidth);
    const height = Math.min(Math.max(MIN_SOURCE_PX, Math.round(rect.height)), this.sourceHeight);
    const x = Math.max(0, Math.min(Math.round(rect.x), this.sourceWidth - width));
    const y = Math.max(0, Math.min(Math.round(rect.y), this.sourceHeight - height));
    return { x, y, width, height };
  }
}

/**
 * Crop the source image data URL to a native-pixel rectangle and return a PNG
 * blob. The crop happens at source resolution regardless of the displayed
 * size, so downstream conversion gets every original pixel of the region.
 */
export async function cropDataUrlToBlob(dataUrl: string, rect: Rect): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Kaynak görsel çözümlenemedi.'));
    img.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(rect.width));
  canvas.height = Math.max(1, Math.round(rect.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Kırpma tuvali oluşturulamadı.');
  ctx.drawImage(
    image,
    Math.round(rect.x),
    Math.round(rect.y),
    canvas.width,
    canvas.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Kırpma görüntüsü üretilemedi.'));
    }, 'image/png');
  });
}
